import { API } from './api';

/**
 * Manifesto preload + fallback pipeline.
 *
 * Strategy (per user brief Fase 3.a):
 *   1. On menu link `mouseenter` / `focus` → fire a background POST /api/manifesto
 *      and cache the result in sessionStorage (24h TTL). This is done via
 *      `preloadManifesto(slug)`.
 *   2. When the PageOpenRitual mounts → call `getManifestoWithFallback(slug, 1800)`
 *      which resolves within at most 1.8s using this priority:
 *        a) sessionStorage cache (instant)
 *        b) still-in-flight preload promise (if it lands within budget)
 *        c) frontend-baked seed (mirror of backend DEFAULT_SEEDS)
 *   3. Never rejects. Always returns a non-empty uppercase string so the
 *      typing animation can start immediately without dead time.
 *
 * The backend's own fallback to Mongo `manifesto_seeds` still applies for
 * subsequent visits (its result populates the sessionStorage on first hit).
 */

// Mirror of backend DEFAULT_SEEDS (backend/manifesto.py). Kept in sync
// intentionally — this is the SAFE instant fallback and its whole purpose
// is to make the ritual feel snappy when the LLM is slow.
const FALLBACK_SEEDS = {
    'cinzia-bio': 'SHE IS… WHO IS SHE?',
    'cinzia-journey': 'EVERY STEP LEAVES A LANGUAGE.',
    'cinzia-vision': 'CONTRADICTION IS A METHOD.',
    'alldesign-chisiamo': 'TWO VISIONS. ONE DIRECTION.',
    'alldesign-vision': 'DESIRE AT FIRST SIGHT.',
    'alldesign-materia': 'AN IDEA BECOMES MATTER.',
    'alldesign-ruolo': 'DESIGN IS ONLY THE BEGINNING.',
    'alldesign-portfolio': 'PROOF, NOT PROMISES.',
};

const CACHE_TTL_MS = 86_400_000; // 24h
const BG_FETCH_TIMEOUT_MS = 8_000; // hard cap on the network fetch itself

// In-memory dedupe: slug -> Promise<string|null>
const inflight = new Map();

function sessionKey(slug) {
    return `manifesto_${slug}`;
}

function readSession(slug) {
    try {
        const raw = sessionStorage.getItem(sessionKey(slug));
        if (!raw) return null;
        const { text, ts } = JSON.parse(raw);
        if (typeof text === 'string' && Date.now() - ts < CACHE_TTL_MS) {
            return text;
        }
    } catch (e) {
        // sessionStorage disabled (private mode / storage full) — non-fatal
        console.debug('[manifestoPreload] read failed:', e);
    }
    return null;
}

function writeSession(slug, text) {
    try {
        sessionStorage.setItem(
            sessionKey(slug),
            JSON.stringify({ text, ts: Date.now() }),
        );
    } catch (e) {
        // storage full / disabled → non-fatal
        console.debug('[manifestoPreload] write failed:', e);
    }
}

function getSessionId() {
    try {
        let s = sessionStorage.getItem('araia_sid');
        if (!s) {
            s = `sid-${Math.random().toString(36).slice(2)}`;
            sessionStorage.setItem('araia_sid', s);
        }
        return s;
    } catch (e) {
        return 'anonymous';
    }
}

/**
 * Fire a background fetch to /api/manifesto for the given slug.
 * Idempotent: repeated calls for the same slug reuse the in-flight promise
 * or short-circuit if the result is already in sessionStorage.
 * Never throws. Safe to call from onMouseEnter/onFocus of many links.
 */
export function preloadManifesto(slug) {
    if (!slug) return;
    if (readSession(slug)) return;
    if (inflight.has(slug)) return;

    const controller = new AbortController();
    const abortT = setTimeout(() => controller.abort(), BG_FETCH_TIMEOUT_MS);

    const p = fetch(`${API}/manifesto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, session_id: getSessionId() }),
        signal: controller.signal,
    })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
            const text = data && typeof data.manifesto === 'string' ? data.manifesto : null;
            if (text) writeSession(slug, text);
            return text;
        })
        .catch(() => null)
        .finally(() => {
            clearTimeout(abortT);
        });

    inflight.set(slug, p);
}

/**
 * Resolve within `timeoutMs` with a non-empty string.
 * Order: sessionStorage → in-flight preload → baked seed.
 * Never rejects.
 */
export function getManifestoWithFallback(slug, timeoutMs = 1800) {
    const cached = readSession(slug);
    if (cached) return Promise.resolve(cached);

    // Ensure a preload is running (in case the ritual was opened without hover,
    // e.g. keyboard navigation or a shared/deep-link URL).
    preloadManifesto(slug);
    const inflightP = inflight.get(slug);

    const seed = FALLBACK_SEEDS[slug] || 'TWO VISIONS. ONE DIRECTION.';

    return new Promise((resolve) => {
        let settled = false;
        const done = (text) => {
            if (settled) return;
            settled = true;
            resolve(text || seed);
        };

        const timer = setTimeout(() => done(seed), timeoutMs);

        if (inflightP) {
            inflightP.then((text) => {
                if (text) {
                    clearTimeout(timer);
                    done(text);
                }
                // null → let the timer fire with seed
            });
        }
    });
}

export { FALLBACK_SEEDS };
